import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useOnline } from "@/lib/offline";
import { flushOutbox, outboxItems } from "@/lib/outbox";

// Sends progress and recordings saved offline as soon as the internet is back, with a small notice.
export const OfflineSync = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const online = useOnline();
  const wasOnline = useRef(online);
  const active = user?.role === "user";

  const report = ({ sent, dropped }, back) => {
    if (dropped) toast.warning(t.sync.dropped(dropped), { id: "yh-sync-dropped" });
    if (sent) toast.success(t.sync.done(sent), { id: "yh-sync" });
    else if (back) toast.success(t.sync.back, { id: "yh-sync" });
  };

  useEffect(() => {
    if (!active) return;
    if (!online) {
      toast.info(t.sync.offline, { id: "yh-sync" });
    } else {
      const back = !wasOnline.current;
      flushOutbox().then((r) => report(r, back)).catch(() => {});
    }
    wasOnline.current = online;
  }, [online, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry every minute while something is still waiting (e.g. the server was unreachable).
  useEffect(() => {
    if (!active || !online) return undefined;
    const id = setInterval(async () => {
      if (!(await outboxItems().catch(() => [])).length) return;
      const r = await flushOutbox().catch(() => null);
      if (r) report(r, false);
    }, 60000);
    return () => clearInterval(id);
  }, [active, online, t]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
